import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, IconButton, TextField, Button, Badge, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useDispatch, useSelector } from "react-redux";
import { removeChatWindow, toggleMinimizeChat } from "../Redux/Slice/dataSlice";
import apiClient from "./apiClient";
import socketService from "socket/newsocketService";
import {
  formatChatTime,
  getMessageText,
  isAdminChatMessage,
  normalizeBoolean,
  normalizeChatList,
  normalizeChatMessage,
  normalizeText,
  sortChatMessages,
} from "./chatMessageHelpers";

const getMessageKey = (msg = {}) =>
  [
    msg?._id ?? "",
    msg?.id ?? "",
    msg?.userId ?? "",
    msg?.time ?? "",
    getMessageText(msg),
    normalizeBoolean(msg?.isAdmin) ? "1" : "0",
    normalizeText(msg?.senderRole || msg?.senderType || msg?.role || msg?.from || msg?.sentBy),
    msg?.adminId ?? "",
    msg?.senderId ?? "",
    msg?.url ?? "",
  ].join("|");

const mergeUniqueMessages = (messages = []) => {
  const seen = new Set();

  return messages.filter((msg) => {
    const key = getMessageKey(msg);
    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const collectHistoryMessages = (escort = {}) => {
  const candidates = [
    escort?.chat,
    escort?.messages,
    escort?.chatHistory,
    escort?.latestEscort?.chat,
    escort?.latestEscort?.messages,
    escort?.latestEscort?.chatHistory,
    escort?.userData?.chat,
    escort?.userData?.messages,
    escort?.userData?.chatHistory,
    escort?.userData?.latestEscort?.chat,
    escort?.userData?.latestEscort?.messages,
    escort?.userData?.latestEscort?.chatHistory,
  ];

  normalizeChatList(escort?.ShortDistance_Escort).forEach((entry) => {
    candidates.push(entry?.chat, entry?.messages, entry?.chatHistory);
  });

  normalizeChatList(escort?.userData?.ShortDistance_Escort).forEach((entry) => {
    candidates.push(entry?.chat, entry?.messages, entry?.chatHistory);
  });

  return candidates.flatMap((candidate) => normalizeChatList(candidate));
};

const ChatWindow = ({ userId, username, index }) => {
  const reduxMinimized = useSelector(
    (state) => state?.data?.activeChats?.[userId]?.isMinimized
  );
  const [localMinimized, setLocalMinimized] = useState(false);
  const isMinimized = typeof reduxMinimized === "boolean" ? reduxMinimized : localMinimized;
  const [unreadCount, setUnreadCount] = useState(0);

  const [isVisible, setIsVisible] = useState(true);
  const [message, setMessage] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const pendingLastMessage = useSelector(
    (state) => state?.data?.activeChats?.[userId]?.lastMessage || null,
  );
  const messagesEndRef = useRef(null);
  const isMinimizedRef = useRef(isMinimized);
  const currentAdminId = String(localStorage.getItem("userId") || "");

  const positionOffset = 320;
  const windowWidth = 300;
  const xOffset = index * positionOffset;
  const yOffset = 0;

  useEffect(() => {
    isMinimizedRef.current = isMinimized;
    if (!isMinimized) {
      setUnreadCount(0);
    }
  }, [isMinimized]);

  const handleToggleMinimize = (e) => {
    if (e) {
      e.stopPropagation();
    }
    setLocalMinimized((prev) => !prev);
    dispatch(toggleMinimizeChat(userId));
  };

  useEffect(() => {
    let isMounted = true;

    const loadChatHistory = async () => {
      if (!userId) {
        setChatMessages([]);
        return;
      }

      console.log("[ChatWindow] loading chat history", { userId, username });
      setIsLoading(true);

      try {
        const response = await apiClient.get("/users/short-escorts");
        const escorts = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.data)
            ? response.data.data
            : [];
        const matchingEscort = escorts.find((escort) => {
          const resolvedIds = [
            escort?.userId,
            escort?.id,
            escort?._id,
            escort?.userObjectKey,
          ]
            .filter(Boolean)
            .map(String);

          return resolvedIds.includes(String(userId));
        });

        const historyMessages = collectHistoryMessages(matchingEscort).map((msg) =>
          normalizeChatMessage(msg, currentAdminId),
        );
        const pendingMessages = pendingLastMessage
          ? [normalizeChatMessage(pendingLastMessage, currentAdminId)]
          : [];
        const messages = sortChatMessages(mergeUniqueMessages([...historyMessages, ...pendingMessages]));

        console.log("[ChatWindow] loaded chat history", {
          userId,
          username,
          escortCount: escorts.length,
          matchedEscortFound: Boolean(matchingEscort),
          historyMessageCount: historyMessages.length,
          pendingMessageCount: pendingMessages.length,
          messageCount: messages.length,
        });

        if (isMounted) {
          setChatMessages((prevMessages) =>
            sortChatMessages(mergeUniqueMessages([...messages, ...prevMessages])),
          );
        }
      } catch (error) {
        console.error("[ChatWindow] Failed to load chat history:", error);
        if (isMounted) {
          setChatMessages([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadChatHistory();

    return () => {
      isMounted = false;
    };
  }, [userId, currentAdminId, username]);

  useEffect(() => {
    if (!pendingLastMessage) {
      return;
    }

    const normalizedPendingMessage = normalizeChatMessage(pendingLastMessage, currentAdminId);

    setChatMessages((prevMessages) =>
      sortChatMessages(mergeUniqueMessages([...prevMessages, normalizedPendingMessage])),
    );
  }, [pendingLastMessage, currentAdminId]);

  useEffect(() => {
    const handleEscortChat = (incomingMessage = {}) => {
      console.log("[ChatWindow] escortchat received", {
        currentUserId: userId,
        currentUsername: username,
        incomingMessage,
      });

      const incomingUserId = String(
        incomingMessage?.userId ||
          incomingMessage?.userObjectKey ||
          incomingMessage?.chatUserId ||
          incomingMessage?.targetUserId ||
          "",
      );
      const incomingUserName = normalizeText(incomingMessage?.userName);
      const currentUserName = normalizeText(username);

      const matchesById = incomingUserId === String(userId);
      const matchesByName = currentUserName && incomingUserName && incomingUserName === currentUserName;

      console.log("[ChatWindow] escortchat match result", {
        currentUserId: userId,
        currentUsername: username,
        incomingUserId,
        incomingUserName,
        matchesById,
        matchesByName,
      });

      if (!matchesById && !matchesByName) {
        return;
      }

      setChatMessages((prevMessages) => {
        const normalizedIncoming = normalizeChatMessage(incomingMessage, currentAdminId);
        const incomingKey = getMessageKey(normalizedIncoming);
        const existingKeys = new Set(prevMessages.map((msg) => getMessageKey(msg)));

        if (existingKeys.has(incomingKey)) {
          return prevMessages;
        }

        if (isMinimizedRef.current && !isAdminChatMessage(normalizedIncoming, currentAdminId)) {
          setUnreadCount((prev) => prev + 1);
        }

        return sortChatMessages([...prevMessages, normalizedIncoming]);
      });
    };

    socketService.on("escortchat", handleEscortChat);
    socketService.on("chat", handleEscortChat);

    return () => {
      socketService.off("escortchat", handleEscortChat);
      socketService.off("chat", handleEscortChat);
    };
  }, [userId]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatMessages]);

  if (!isVisible) return null;

  const isAdminMessage = (msg = {}) => isAdminChatMessage(msg, currentAdminId);

  const getMessageSide = (msg = {}) => (isAdminMessage(msg) ? "right" : "left");

  const handleInput = async () => {
    if (message.trim() === "") return;

    const currentTime = new Date().toISOString();
    const chatText = message.trim();
    const payload = {
      userId,
      message: chatText,
      msg: chatText,
      text: chatText,
      isAdmin: true,
      adminId: localStorage.getItem("userId"),
      senderId: localStorage.getItem("userId"),
      senderRole: "admin",
      senderType: "admin",
      sentBy: "admin",
      from: "ME",
      state: localStorage.getItem("state") || "",
      userName: username,
      time: currentTime,
    };

    try {
      console.log("[ChatWindow] emitting chat", {
        userId,
        username,
        payload,
      });
      const response = await socketService.emitWithAck("chat", payload);
      console.log("[ChatWindow] chat ack response", {
        userId,
        username,
        response,
      });
      if (response?.success === false) {
        console.error("[ChatWindow] Chat send failed:", response?.message || "Unknown error");
        return;
      }

      setChatMessages((prevMessages) => {
        const incomingKey = getMessageKey(payload);
        if (prevMessages.some((msg) => getMessageKey(msg) === incomingKey)) {
          return prevMessages;
        }

        return sortChatMessages([...prevMessages, normalizeChatMessage(payload, currentAdminId)]);
      });
      setMessage("");
    } catch (error) {
      console.error("[ChatWindow] Failed to send chat message:", error);
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 10 + yOffset,
        right: 100 + xOffset,
        width: windowWidth,
        border: "1px solid #ccc",
        borderRadius: 2,
        boxShadow: 3,
        backgroundColor: "white",
        overflow: "hidden",
        zIndex: 2000,
      }}
    >
      {/* Header */}
      <Box
        onClick={isMinimized ? handleToggleMinimize : undefined}
        sx={{
          backgroundColor: "#3f51b5",
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px 12px",
          cursor: isMinimized ? "pointer" : "default",
          userSelect: "none",
        }}
        title={isMinimized ? "Click to maximize" : undefined}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1, mr: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{
              color: "#FFFFFF",
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={username}
          >
            {username}
          </Typography>
          {isMinimized && unreadCount > 0 && (
            <Badge
              badgeContent={unreadCount}
              color="error"
              sx={{
                ml: 1,
                "& .MuiBadge-badge": {
                  fontSize: "0.75rem",
                  height: "18px",
                  minWidth: "18px",
                  padding: "0 4px",
                },
              }}
            />
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
          <Tooltip title={isMinimized ? "Maximize" : "Minimize"}>
            <IconButton
              size="small"
              color="inherit"
              aria-label={isMinimized ? "Maximize chat window" : "Minimize chat window"}
              sx={{
                color: "#FFFFFF",
                padding: "4px",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                },
              }}
              onClick={handleToggleMinimize}
            >
              {isMinimized ? (
                <KeyboardArrowUpIcon sx={{ fontSize: "1.2rem" }} />
              ) : (
                <RemoveIcon sx={{ fontSize: "1.2rem" }} />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton
              size="small"
              color="inherit"
              aria-label="Close chat window"
              sx={{
                color: "#FFFFFF",
                padding: "4px",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                dispatch(removeChatWindow(userId));
              }}
            >
              <CloseIcon sx={{ fontSize: "1.2rem" }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {!isMinimized && (
        <Box
          sx={{
            height: 250,
            padding: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            backgroundColor: "#EDEFF2",
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              padding: 1,
              borderRadius: 3,
            }}
          >
            {isLoading ? (
              <Typography variant="body2">Loading chat history...</Typography>
            ) : chatMessages.length === 0 ? (
              <Typography variant="body2">No chat messages yet.</Typography>
            ) : (
              chatMessages.map((msg, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    flexDirection: isAdminMessage(msg) ? "row-reverse" : "row",
                    alignItems: "center",
                    marginBottom: 1,
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: "70%",
                      wordWrap: "break-word",
                      backgroundColor: isAdminMessage(msg) ? "#E6E6E6" : "#f8d7da",
                      borderRadius: 2,
                      padding: 1,
                      marginLeft: isAdminMessage(msg) ? 0 : 1,
                      marginRight: isAdminMessage(msg) ? 1 : 0,
                    }}
                  >
                    {msg.url ? (
                      <Typography
                        variant="body2"
                        sx={{
                          textAlign: getMessageSide(msg),
                          color: "#1976d2",
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                        component="a"
                        href={msg.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {msg.message}
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ textAlign: getMessageSide(msg), color: isAdminMessage(msg) ? "#000" : "inherit" }}
                      >
                        {msg.message}
                      </Typography>
                    )}

                    <Typography
                      variant="caption"
                      sx={{
                        textAlign: getMessageSide(msg),
                        color: "gray",
                      }}
                    >
                      {formatChatTime(msg?.time)}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
            <div ref={messagesEndRef} />
          </Box>

          <TextField
            placeholder="Type a message..."
            variant="outlined"
            fullWidth
            size="small"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleInput();
              }
            }}
          />

          <Button
            onClick={handleInput}
            type="button"
            variant="contained"
            sx={{
              width: "100%",
              backgroundColor: "#1976d2",
              color: "#ffffff",
              fontWeight: "bold",
              fontSize: "16px",
              textTransform: "none",
              borderRadius: "8px",
              "&:hover": { backgroundColor: "#1565c0" },
            }}
          >
            Send
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ChatWindow;
